import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useGeo } from "@/lib/useGeo";
import useContentLanguage, { CONTENT_LANGUAGES } from "@/lib/useContentLanguage";
import { MapPin, Languages, Send } from "lucide-react";

const CATEGORIES = ["housing", "food", "education", "medical", "seniors", "environment", "children", "other"];

// Sibling / headless-CMS destinations. Only visible to admins in the compose UI.
// Keep the slugs in sync with backend ALLOWED_SITE_SOURCES (cms.py) and the
// site_source normalisation in routes/content.py.
const PUBLISH_TARGETS = [
  { value: "main",       label: "Social Pulse (flagship)" },
  { value: "kerala",     label: "Kerala Pulse" },
  { value: "foodpulse",  label: "Food Pulse" },
  { value: "indias",     label: "India's Projects" },
];

export const ShareStoryForm = ({ onCreated }) => {
  const { user } = useAuth();
  const { geo } = useGeo();
  const readerLang = useContentLanguage();
  const [form, setForm] = useState({
    title: "",
    body: "",
    category: "food",
    image_url: "",
    site_source: "main",
    content_language: readerLang || "en",
  });
  const [saving, setSaving] = useState(false);

  // Pre-select the reader's active translation language when it changes
  // BEFORE the user has typed a body. Do NOT override once they've typed.
  useEffect(() => {
    setForm((f) => (f.body.length ? f : { ...f, content_language: readerLang || "en" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readerLang]);

  if (!user) {
    return (
      <div
        data-testid="share-story-signin"
        className="wofy-card mt-10 text-center"
        style={{ background: "var(--wofy-surface)" }}
      >
        <div className="overline">SHARE YOUR JOY STORY</div>
        <h3 className="font-display text-2xl mt-2">Sign in to share a story.</h3>
        <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          We&rsquo;ll tag your story with your city so it can show up on the Community Impact Map.
        </p>
        <div className="mt-4 flex justify-center gap-3">
          <Link to="/login?next=/stories" className="wofy-btn-primary">Sign in</Link>
          <Link to="/register?next=/stories" className="wofy-btn-ghost">Create account</Link>
        </div>
      </div>
    );
  }

  const locLabel = geo
    ? [geo.city, geo.country_name].filter(Boolean).join(", ") || geo.country_name || "location unavailable"
    : "detecting…";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Please add a title and a story");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        author_name: user.name,
        city: geo?.city || null,
        country: geo?.country_name || null,
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
      };
      // Non-admins can never publish to a sibling site — force flagship.
      if (!user.is_admin) payload.site_source = "main";
      await http.post("/stories", payload);
      const dest = PUBLISH_TARGETS.find((t) => t.value === payload.site_source);
      toast.success(
        payload.site_source && payload.site_source !== "main"
          ? `Published to ${dest?.label || payload.site_source}!`
          : "Your Joy Story is live!"
      );
      setForm({ title: "", body: "", category: "food", image_url: "", site_source: "main", content_language: readerLang || "en" });
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save story");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="share-story-form"
      className="wofy-card mt-10 grid gap-4"
    >
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="overline">SHARE YOUR JOY STORY</div>
          <h3 className="font-display text-2xl mt-1">A moment worth remembering.</h3>
        </div>
        <div
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs"
          style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)", border: "1px solid var(--wofy-border)" }}
          data-testid="story-location-pill"
        >
          <MapPin className="w-3 h-3" style={{ color: "var(--wofy-primary)" }} />
          Tagging as <span className="font-medium ml-1" style={{ color: "var(--wofy-text)" }}>{locLabel}</span>
        </div>
      </div>

      <input
        data-testid="story-title-input"
        value={form.title}
        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        placeholder="Title — e.g. The neighbor who wouldn't take no for an answer"
        className="wofy-input"
        maxLength={140}
      />
      <textarea
        data-testid="story-body-input"
        value={form.body}
        onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
        placeholder="Tell us what happened, who helped, and what it meant."
        className="wofy-input min-h-[130px]"
        maxLength={2000}
      />
      <label className="block text-sm" data-testid="story-content-language-wrap">
        <div className="mb-1.5 font-medium flex items-center gap-1.5">
          <Languages className="w-3.5 h-3.5 opacity-70" />
          You're writing in
        </div>
        <select
          data-testid="story-content-language"
          value={form.content_language}
          onChange={(e) => setForm((f) => ({ ...f, content_language: e.target.value }))}
          className="wofy-input"
        >
          {CONTENT_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </label>
      <div className="grid md:grid-cols-2 gap-4">
        <label className="block text-sm">
          <div className="mb-1.5 font-medium">Category</div>
          <select
            data-testid="story-category-input"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="wofy-input"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <div className="mb-1.5 font-medium">Image URL (optional)</div>
          <input
            data-testid="story-image-input"
            value={form.image_url}
            onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
            placeholder="https://…"
            className="wofy-input"
          />
        </label>
      </div>
      {user.is_admin && (
        <label className="block text-sm" data-testid="story-publish-target-wrap">
          <div className="mb-1.5 font-medium flex items-center justify-between">
            <span>Publish to</span>
            <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
              Admin only · hub-and-spoke
            </span>
          </div>
          <select
            data-testid="story-publish-target"
            value={form.site_source}
            onChange={(e) => setForm((f) => ({ ...f, site_source: e.target.value }))}
            className="wofy-input"
          >
            {PUBLISH_TARGETS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
      )}
      <div className="flex justify-end">
        <button type="submit" data-testid="story-submit-btn" disabled={saving} className="wofy-btn-primary">
          {saving ? "Publishing…" : (<>Publish story <Send className="w-4 h-4" /></>)}
        </button>
      </div>
    </form>
  );
};
